// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
import { useEffect, useRef } from "react";
import { setOnboardingMuted } from "@/services/playback/audioSession";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { AGE_ONBOARDING_QUERY_KEY, useAgeOnboarding } from "@/hooks/useAgeOnboarding";
import AuthPanel from "@/components/auth/AuthPanel";
import CookieSettingsLink from "@/components/cookies/CookieSettingsLink";
import OnboardingWizard from "@/components/onboarding/OnboardingWizard";

const PROFILE_COMPLETION_KEY = "profile-completion";

/**
 * Le profil est considéré comme finalisé quand le nom et le pseudo publics
 * sont renseignés (bio et photo restent facultatives).
 */
const useProfileCompletion = (userId?: string) =>
  useQuery({
    queryKey: [PROFILE_COMPLETION_KEY, userId],
    enabled: !!userId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, username")
        .eq("id", userId!)
        .maybeSingle();

      if (error) throw error;
      return !!data?.full_name?.trim() && !!data?.username?.trim();
    },
  });

interface AuthGateProps {
  /**
   * Informe le parent que l'accès est verrouillé (voile + popup affichés),
   * afin de figer les interactions du feed derrière.
   */
  onLockChange?: (locked: boolean) => void;
}

/**
 * Voile + popup posés sur le feed public : le contenu reste visible mais aucune
 * interaction n'est possible tant que le compte n'est pas connecté ET finalisé.
 */
export const AuthGate = ({ onLockChange }: AuthGateProps) => {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const { state: ageState, isLoading: ageLoading, refetch: refetchAge } = useAgeOnboarding();
  const { data: profileComplete, isLoading: profileLoading } = useProfileCompletion(user?.id);

  /**
   * Lien « mot de passe oublié » : Supabase crée une session de récupération,
   * donc `user` existe déjà — sans ce cas, le voile se referme et le lien
   * renvoie au feed au lieu du formulaire de nouveau mot de passe.
   */
  const isRecovery =
    new URLSearchParams(window.location.search).get("reset") === "true" ||
    window.location.hash.includes("type=recovery");

  const needsAge = !!user && !ageLoading && !ageState.complete;
  const needsIdentity = !!user && !profileLoading && profileComplete === false;
  const stillChecking = loading || (!!user && (ageLoading || profileLoading));
  const locked = isRecovery || (!stillChecking && (!user || needsAge || needsIdentity));

  useEffect(() => {
    onLockChange?.(locked);
  }, [locked, onLockChange]);

  // Voile affiché : fil muet. À sa fermeture, accueil rechargé (son neuf, bulle de retour).
  const wasLockedRef = useRef(false);
  useEffect(() => {
    if (locked) {
      wasLockedRef.current = true;
      setOnboardingMuted(true);
    } else if (wasLockedRef.current) {
      window.location.replace("/");
    }
  }, [locked]);

  if (!locked) return null;

  const handleCompleted = () => {
    refetchAge();
    queryClient.invalidateQueries({ queryKey: [PROFILE_COMPLETION_KEY, user?.id] });
    queryClient.invalidateQueries({ queryKey: [AGE_ONBOARDING_QUERY_KEY, user?.id] });
  };

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto bg-black/30 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
    >
      {/* Défilement de page : le haut de la carte reste toujours atteignable. */}
      <div className="flex min-h-full flex-col items-center justify-center gap-3 p-4">
        {isRecovery ? (
          <AuthPanel embedded />
        ) : user ? (
          <OnboardingWizard
            needsAge={needsAge}
            needsIdentity={needsIdentity}
            onCompleted={handleCompleted}
          />
        ) : (
          <AuthPanel embedded />
        )}

        {/* Aucun pied de page sur le feed : le lien cookies suit la carte. */}
        <CookieSettingsLink />
      </div>
    </div>
  );
};

export default AuthGate;
